import {
  useSearchContext,
  useSidebarCollapse
} from "./chunk-WTO3J3EA.js";
import {
  useI18n
} from "./chunk-3P4ASMQP.js";
import {
  cn
} from "./chunk-S2JEC2ZU.js";

// src/nav.tsx
import { cva as cva2 } from "class-variance-authority";
import {
  MenuIcon,
  SearchIcon,
  SidebarCloseIcon,
  SidebarOpenIcon
} from "lucide-react";
import Link from "next-docs-zeta/link";
import { SidebarTrigger } from "next-docs-zeta/sidebar";
import { usePathname } from "next/navigation";

// src/components/theme-toggle.tsx
import { cva } from "class-variance-authority";
import { MoonIcon, SunIcon } from "lucide-react";
import { useTheme } from "next-themes";
import { useCallback } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
var buttonVariants = cva("h-7 w-7 rounded-full p-1.5 text-muted-foreground", {
  variants: {
    dark: {
      true: "dark:bg-accent dark:text-accent-foreground",
      false: "bg-accent text-accent-foreground dark:bg-transparent dark:text-muted-foreground"
    }
  }
});
function ThemeToggle({
  className,
  ...props
}) {
  const { setTheme, resolvedTheme } = useTheme();
  const onToggle = useCallback(() => {
    setTheme(resolvedTheme === "dark" ? "light" : "dark");
  }, [setTheme, resolvedTheme]);
  return /* @__PURE__ */ jsxs(
    "button",
    {
      type: "button",
      className: cn(
        "inline-flex items-center rounded-full border p-0.5",
        className
      ),
      "aria-label": "Toggle Theme",
      onClick: onToggle,
      ...props,
      children: [
        /* @__PURE__ */ jsx(SunIcon, { className: cn(buttonVariants({ dark: false })) }),
        /* @__PURE__ */ jsx(MoonIcon, { className: cn(buttonVariants({ dark: true })) })
      ]
    }
  );
}

// src/nav.tsx
import { Fragment, jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var itemVariants = cva2(
  "rounded-md p-1.5 hover:bg-accent hover:text-accent-foreground [&_svg]:h-5 [&_svg]:w-5"
);
function Nav({
  title,
  url = "/",
  links,
  items,
  transparent = false,
  enableSidebar = true,
  collapsibleSidebar = true,
  children
}) {
  return /* @__PURE__ */ jsx2(
    "header",
    {
      className: cn(
        "sticky top-0 z-50 h-16 border-b transition-colors",
        transparent ? "border-transparent" : "border-foreground/10 bg-background/80 backdrop-blur-sm"
      ),
      children: /* @__PURE__ */ jsxs2("nav", { className: "container flex h-full flex-row items-center gap-4", children: [
        /* @__PURE__ */ jsx2(Link, { href: url, className: "inline-flex items-center font-medium", children: title }),
        children,
        items?.map((item) => /* @__PURE__ */ jsx2(NavItem, { ...item }, item.href)),
        /* @__PURE__ */ jsxs2("div", { className: "ml-auto flex flex-row items-center md:gap-2", children: [
          /* @__PURE__ */ jsx2(SearchToggle, {}),
          /* @__PURE__ */ jsx2(ThemeToggle, { className: cn(enableSidebar && "max-md:hidden") }),
          enableSidebar ? /* @__PURE__ */ jsx2(SidebarToggle, { collapsible: collapsibleSidebar }) : null,
          /* @__PURE__ */ jsx2("div", { className: "flex flex-row items-center border-l pl-2 max-md:hidden", children: links?.map((item) => /* @__PURE__ */ jsx2(NavLink, { ...item }, item.href)) })
        ] })
      ] })
    }
  );
}
var shortcut = cva2("rounded-md border bg-background px-1.5");
function SearchToggle() {
  const [setOpenSearch] = useSearchContext();
  const { search = "Search" } = useI18n().text;
  return /* @__PURE__ */ jsxs2(Fragment, { children: [
    /* @__PURE__ */ jsx2(
      "button",
      {
        type: "button",
        className: cn(itemVariants({ className: "md:hidden" })),
        "aria-label": "Open Search",
        onClick: () => {
          setOpenSearch(true);
        },
        children: /* @__PURE__ */ jsx2(SearchIcon, {})
      }
    ),
    /* @__PURE__ */ jsxs2(
      "button",
      {
        type: "button",
        className: "inline-flex w-[240px] items-center gap-2 rounded-full border bg-secondary/50 p-1.5 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground max-md:hidden",
        onClick: () => {
          setOpenSearch(true);
        },
        children: [
          /* @__PURE__ */ jsx2(SearchIcon, { "aria-label": "Open Search", className: "ml-1 h-4 w-4" }),
          search,
          /* @__PURE__ */ jsxs2("div", { className: "ml-auto inline-flex gap-0.5 text-xs", children: [
            /* @__PURE__ */ jsx2("kbd", { className: shortcut(), children: "\u2318" }),
            /* @__PURE__ */ jsx2("kbd", { className: shortcut(), children: "K" })
          ] })
        ]
      }
    )
  ] });
}
function SidebarToggle({ collapsible }) {
  const [open, setOpen] = useSidebarCollapse();
  return /* @__PURE__ */ jsxs2(Fragment, { children: [
    /* @__PURE__ */ jsx2(
      SidebarTrigger,
      {
        "aria-label": "Toggle Sidebar",
        className: cn(itemVariants({ className: "md:hidden" })),
        children: /* @__PURE__ */ jsx2(MenuIcon, {})
      }
    ),
    collapsible ? /* @__PURE__ */ jsx2(
      "button",
      {
        type: "button",
        "aria-label": "Toggle Sidebar",
        onClick: () => {
          setOpen(!open);
        },
        className: "rounded-full border bg-secondary/50 p-1.5 hover:bg-accent hover:text-accent-foreground max-md:hidden",
        children: open ? /* @__PURE__ */ jsx2(SidebarCloseIcon, { className: "h-5 w-5" }) : /* @__PURE__ */ jsx2(SidebarOpenIcon, { className: "h-5 w-5" })
      }
    ) : null
  ] });
}
function NavItem(props) {
  const pathname = usePathname();
  const isActive = props.href === pathname || pathname.startsWith(`${props.href}/`);
  return /* @__PURE__ */ jsx2(
    Link,
    {
      href: props.href,
      external: props.external,
      className: cn(
        "text-sm text-muted-foreground max-lg:hidden",
        isActive ? "font-medium text-accent-foreground" : "transition-colors hover:text-accent-foreground"
      ),
      children: props.children
    }
  );
}
function NavLink(props) {
  return /* @__PURE__ */ jsx2(
    Link,
    {
      "aria-label": props.label,
      href: props.href,
      external: props.external,
      className: cn(itemVariants()),
      children: props.icon
    }
  );
}

export {
  ThemeToggle,
  itemVariants,
  Nav
};
